import type { OrganizationAuditLogQuery } from '@osteopro/shared';

/** Compte, cabinet actif et ce qui en dépend directement : droits, abonnement, préférences. */

export const authKeys = {
  all: () => ['auth'] as const,
  identity: () => [...authKeys.all(), 'identity'] as const,
  /** Compte de codes de secours restants. Jamais les codes : ils ne sont plus lisibles. */
  backupCodes: () => [...authKeys.all(), 'backup-codes'] as const,
};

/** Le cabinet actif n'est jamais désigné par son identifiant : la session le porte déjà. */
export const organizationKeys = {
  all: () => ['organizations'] as const,
  current: () => [...organizationKeys.all(), 'current'] as const,
  locations: () => [...organizationKeys.current(), 'locations'] as const,
  /** Les octets du logo. Jamais conservés : l'URL d'objet est révoquée au démontage de l'écran. */
  logo: () => [...organizationKeys.current(), 'logo'] as const,
  members: () => [...organizationKeys.current(), 'members'] as const,
  invitations: () => [...organizationKeys.current(), 'invitations'] as const,
  /** Remplacements datés : l'écran des membres les écrit, l'agenda en dépend. */
  replacements: () => [...organizationKeys.current(), 'replacements'] as const,
  ownProfile: () => [...organizationKeys.members(), 'me', 'profile'] as const,
  /** Réglage de l'inscription publique : le lien est un secret, réservé au propriétaire. */
  publicRegistration: () => [...organizationKeys.current(), 'public-registration'] as const,
  integrations: () => [...organizationKeys.current(), 'integrations'] as const,
  /** Journal d'activité du cabinet, réservé au propriétaire, indexé par ses filtres. */
  auditLog: (params: Partial<OrganizationAuditLogQuery>) =>
    [...organizationKeys.current(), 'audit-log', params] as const,
};

/** Tâches personnelles du membre dans le cabinet actif : une seule liste, bornée, sans filtre. */
export const taskKeys = {
  all: () => ['tasks'] as const,
};

/** Formulaire public d'auto-inscription : hors session, donc hors de la hiérarchie du cabinet. */
export const publicRegistrationKeys = {
  all: () => ['public-registration'] as const,
  form: (token: string) => [...publicRegistrationKeys.all(), token] as const,
};

/** Lecture publique d'un jeton d'invitation : hors session, donc hors de la hiérarchie du cabinet. */
export const invitationKeys = {
  all: () => ['invitations'] as const,
  preview: (token: string) => [...invitationKeys.all(), 'preview', token] as const,
};

/**
 * Recherche globale de l'en-tête. Une racine à part : elle traverse dossiers et consultations, et
 * aucune écriture de l'un ou de l'autre n'a à la connaître — l'entrée se relit à chaque recherche.
 */
export const searchKeys = {
  all: () => ['search'] as const,
  global: (term: string) => [...searchKeys.all(), 'global', term] as const,
};

/** Suggestions d'adresses : une entrée par saisie et par langue, que rien n'invalide. */
export const addressKeys = {
  all: () => ['addresses'] as const,
  suggest: (term: string, lang: string) => [...addressKeys.all(), 'suggest', term, lang] as const,
};

/**
 * Tableau de bord. Chaque agrégat a sa propre entrée : le bandeau du jour se rafraîchit après une
 * action d'agenda sans invalider les graphiques, qui coûtent douze mois d'agrégation.
 */
export const dashboardKeys = {
  all: () => ['dashboard'] as const,
  summary: () => [...dashboardKeys.all(), 'summary'] as const,
  today: (date: string | null, mine: boolean) =>
    [...dashboardKeys.all(), 'today', date, mine] as const,
  charts: (months: number) => [...dashboardKeys.all(), 'charts', months] as const,
  insights: () => [...dashboardKeys.all(), 'insights'] as const,
  pediatric: () => [...dashboardKeys.all(), 'pediatric'] as const,
  operations: () => [...dashboardKeys.all(), 'operations'] as const,
  birthdays: () => [...dashboardKeys.all(), 'birthdays'] as const,
  birthdayDraft: (patientId: string) =>
    [...dashboardKeys.all(), 'birthdays', 'draft', patientId] as const,
};

/**
 * Offres et droits. Le catalogue n'a pas de clé : il vit dans `@osteopro/shared` et ne se lit pas.
 * Seuls les droits du cabinet actif viennent du serveur, et ils changent avec l'usage — un dossier
 * créé bouge un compteur — d'où une entrée qui s'invalide avec les écritures qui les font bouger.
 */
export const planKeys = {
  all: () => ['plans'] as const,
  entitlements: () => [...planKeys.all(), 'entitlements'] as const,
};

/**
 * Abonnement du cabinet actif. Entrée distincte des droits : ceux-ci sont lus par tous les écrans
 * et doivent rester légers. L'abonnement ne se lit qu'à deux endroits. Il change quand on met à
 * jour le tarif déjà ouvert, et le rappel du prestataire confirme la même écriture.
 */
export const subscriptionKeys = {
  all: () => ['subscription'] as const,
  current: () => [...subscriptionKeys.all(), 'current'] as const,
};

/** Mise en route du membre dans le cabinet actif : un seul état, calculé par le serveur. */
export const onboardingKeys = {
  all: () => ['onboarding'] as const,
  status: () => [...onboardingKeys.all(), 'status'] as const,
};

/** Préférences du compte : thème, langue et disposition du tableau de bord. Hors cabinet. */
export const preferenceKeys = {
  all: () => ['preferences'] as const,
  /** Réglages d'affichage de l'agenda : même ressource « moi », sa propre entrée de cache. */
  agenda: () => [...preferenceKeys.all(), 'agenda'] as const,
};
